import { useBoolean, useRequest } from 'ahooks';
import {
  Alert,
  Button,
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  InputProps,
  Modal,
  Radio,
  Rate,
  Row,
  Select,
  Space,
  Spin,
  Tooltip,
  message,
} from 'antd';
import {
  ApplicationTypeEnum,
  CustomerDTO,
  CustomerEventTypeEnum,
  CustomerGroupEnum,
  CustomerPoolEnum,
  CustomerStateEnum,
  GenderEnum,
  customerController,
} from '../api';
import { useCompany } from '../hooks/company';
import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  customerGroupMapping,
  extraKeys,
  filterCustomGroupMapping,
  generateExtra,
} from '../constants';
import CustomerStateSelect from './CustomerStateSelect';
import { EyeFill, PhoneFill } from 'antd-mobile-icons';
import LastEvent, { EventTimelineAutoLoad, LastEventDrawer } from './LastEvent';
import { InfoCircleOutlined } from '@ant-design/icons';
import CustomerAlarm, { CustomerAlarmClock } from './CustomerAlarm';

const refreshContext = createContext<{ refresh?: () => void }>({});

export const userRefreshContext = () => {
  return useContext(refreshContext);
};

export const RefreshProvider = ({
  children,
  refresh,
}: PropsWithChildren<{ refresh: () => void }>) => {
  return (
    <refreshContext.Provider value={{ refresh }}>
      {children}
    </refreshContext.Provider>
  );
};

const MakeCall = ({ customerId }: { customerId: I.ID }) => {
  const { id: companyId } = useCompany();
  const { run: makeCall, loading } = useRequest(
    () => {
      return customerController.callCustomer(companyId, customerId);
    },
    {
      refreshDeps: [customerId],
      manual: true,
      onError: (e) => {
        message.error(e.message);
      },
      onSuccess: () => {
        message.success('拨打成功，请去 app 查看');
      },
    },
  );
  return (
    <Button
      type="link"
      icon={<PhoneFill />}
      loading={loading}
      onClick={() => makeCall()}
    >
      打电话
    </Button>
  );
};

const Comment = ({
  onChange,
  value,
  hitSensitiveWord,
  readOnly,
}: {
  onChange: (v: string) => void;
  value?: string;
  hitSensitiveWord: boolean;
  readOnly?: boolean;
}) => {
  const tips = ['无人接听', '直接挂断', '不需要', '关机'];

  const change = useCallback(
    (v: string) => {
      onChange(v);
    },
    [onChange],
  );

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <div>
        <Space>
          {tips.map((v) => (
            <Button key={v} type="link" onClick={() => change(v)}>
              {v}
            </Button>
          ))}
        </Space>
      </div>
      {hitSensitiveWord && (
        <Alert message="请不要输入违禁词" type="warning" showIcon />
      )}
      <Input.TextArea
        placeholder="跟进备注"
        value={value}
        status={hitSensitiveWord ? 'error' : undefined}
        onChange={(v) => change(v.target.value)}
        rows={5}
        autoComplete="off"
        readOnly={readOnly}
      />
    </Space>
  );
};

const convertStarState = (star?: number) => {
  switch (star) {
    case undefined:
    case 0:
      return [CustomerStateEnum.WAIT_FOLLOW_UP];
    case 1:
      return [
        CustomerStateEnum.UNQUALIFIED,
        CustomerStateEnum.VISITED,
        CustomerStateEnum.SIGNED,
        CustomerStateEnum.EXAMINING,
        CustomerStateEnum.INVALID,
      ];
    case 2:
    case 3:
    case 4:
    case 5:
      return [
        CustomerStateEnum.LOAN,
        CustomerStateEnum.SIGNED,
        CustomerStateEnum.VISITED,
        CustomerStateEnum.EXAMINING,
        CustomerStateEnum.WAIT_SIGN,
      ];
    default:
      return undefined;
  }
};

export default ({
  id,
  open,
  onCancel,
  next,
  withComment,
  updateCustomerNamePermission,
  showMobile,
  readOnly,
}: {
  open?: boolean;
  next?: (currentId: I.ID) => void;
  onCancel: () => void;
  readOnly?: boolean;
  id: I.ID;
  withComment: boolean;
  updateCustomerNamePermission?: boolean;
  showMobile?: boolean;
}) => {
  const {
    id: companyId,
    config: companyConfig,
    currentUser: { isSuperAdmin },
    systemConfig,
  } = useCompany();

  const [comment, setComment] = useState<string>();
  const [dropPublicPool, setDropPublicPool] = useState<boolean>();

  const [mobile, setMobile] = useState<string>();

  const { refresh: refreshParent } = useContext(refreshContext);

  const [form] = Form.useForm();

  const currentStar = Form.useWatch<number>('star', form);

  const starState = useMemo(() => {
    return convertStarState(currentStar);
  }, [currentStar]);

  const hitSensitiveWord = useMemo(() => {
    if (
      comment &&
      companyConfig?.sensitiveWordList?.find((item) => comment.includes(item))
    ) {
      return true;
    }
    return false;
  }, [comment, companyConfig]);

  const {
    data: customer,
    loading: getLoading,
    run,
    refresh,
  } = useRequest(
    (id: I.ID) => {
      return customerController.getDetail(companyId, id);
    },
    {
      manual: true,
      onSuccess: () => {
        form.resetFields();
      },
    },
  );

  const disableState = useMemo(() => {
    if (isSuperAdmin) {
      return false;
    }
    return customer?.state === CustomerStateEnum.LOAN;
  }, [customer, isSuperAdmin]);

  const {
    data: events,
    loading: eventsLoading,
    run: loadEvents,
  } = useRequest(
    () => {
      return customerController.listEvent(companyId, id);
    },
    {
      manual: true,
    },
  );

  useEffect(() => {
    setMobile(customer?.mobile);
  }, [customer?.mobile, setMobile]);

  useEffect(() => {
    if (open) {
      setDropPublicPool(false);
      setComment(undefined);
      loadEvents();
      run(id);
    }
  }, [open, id]);

  const { loading: updateLoading, runAsync: update } = useRequest(
    customerController.updateCustomer,
    {
      manual: true,
      refreshDeps: [id, companyId],
      // 注意：不要在这里 refresh()/refreshParent?.()——onSubmit 是 update -> dropToPublic/addEvent
      // 串联流程，update.onSuccess 早于 dropToPublic 落库。父表格此时 reload 会读到 pool 仍为 BIZ
      // 的旧状态，导致勾选「抛入公共池」后列表未刷新。统一放到 onSubmit 链路末尾兜底。
      onError: (e) => {
        message.error(e.message);
      },
    },
  );

  const { loading: createEventLoading, runAsync: addEvent } = useRequest(
    (comment: string) => {
      return customerController.addEvent(
        companyId,
        id,
        CustomerEventTypeEnum.COMMENT,
        comment,
      );
    },
    {
      manual: true,
      refreshDeps: [comment, id, companyId],
    },
  );

  // 抛入公共池走单一原子接口：后端在同一事务里完成 pool 变更 + 拆分 event 写入，
  // 保证不会出现 pool 已改但 event 缺失的状态不一致（旧实现 update + addEvent 两步
  // 任何一步失败都会留下不一致）。
  // hasRealComment 决定后端是否拆分为 COMMENT + DROP_PUBLIC_POOL：
  // 用户实际写了评论 → true（拆分），未写评论用兜底文案 → false（单条保留文案）。
  // 注意 hasRealComment=false 时后端仍然保留 commentText 到单条 DROP_PUBLIC_POOL.comment，
  // 不会丢失文本——这是与后端 #161 协商好的协议。
  const { loading: dropPublicPoolLoading, runAsync: dropToPublic } = useRequest(
    (commentText: string, hasRealComment: boolean) => {
      return customerController.dropToPublic(companyId, id, commentText, hasRealComment);
    },
    {
      manual: true,
      refreshDeps: [id, companyId],
    },
  );

  const loading =
    getLoading || updateLoading || createEventLoading || dropPublicPoolLoading;

  const onSubmit = useCallback(
    (customer?: CustomerDTO, comment?: string) => {
      return form
        .validateFields()
        .then((values) => {
          // pool 字段不在 update 里改：勾选"抛入公共池"由下面的原子接口处理。
          // 这样 update 失败也只是字段没更新，与 pool/event 一致性无关。
          const dto = {
            ...customer,
            ...values,
            mobile,
            channel: customer?.channel,
            extra: undefined,
            pool: customer?.pool,
          };
          Object.entries(extraKeys).forEach(([key]) => {
            dto[key] = values?.extra.includes(key);
          });

          return update(companyId, id, dto)
            .then(async () => {
              if (dropPublicPool) {
                // 原子：pool=PUBLIC + COMMENT（有评论时）+ DROP_PUBLIC_POOL 同事务落盘。
                // 纯空白 comment（例如 "   "）必须按"没填评论"处理：trim 后判定 hasRealComment，
                // 否则会把空白文本传给后端、被拆出空白 COMMENT 事件。
                // 兜底文案 "抛入公共池" 仅在 hasRealComment=false 时作为单条 event 的展示文本，
                // 后端不再依赖字面量匹配（前后端文案变更解耦）。
                const normalizedComment = comment?.trim();
                const hasRealComment = Boolean(normalizedComment);
                await dropToPublic(normalizedComment || '抛入公共池', hasRealComment);
              } else if (comment) {
                await addEvent(comment);
              }
            })
            .then(() => {
              // 所有写入（update + 可能的 dropToPublic/addEvent）落库后再刷新，避免父表格 reload
              // 抢跑后端 pool 变更，造成「勾选抛入公共池后列表未刷新」的偶现 race。
              refresh();
              refreshParent?.();
              message.success('更新成功');
            });
        })
        .catch((e) => {
          if (e.errorFields && e.errorFields.length) {
            message.error(e.errorFields[0].errors[0]);
          }
          return Promise.reject(e);
        });
    },
    [customer, mobile, dropPublicPool, form, update, addEvent, dropToPublic, refresh, refreshParent],
  );

  useEffect(() => {
    form.resetFields();
  }, [customer, open]);

  const groupOptions = Object.entries(customerGroupMapping)
    .filter((item) =>
      filterCustomGroupMapping(
        item[0] as CustomerGroupEnum,
        systemConfig?.applicationType,
      ),
    )
    .map(([value, label]) => ({
      label,
      value,
    }));

  const onSubmitAndNext = useCallback(
    (comment?: string) => {
      return onSubmit(customer, comment)
        .then(() => {
          next?.(id);
        })
        .catch((e) => message.error(e.message));
    },
    [id, customer, mobile, onSubmit],
  );

  return (
    <Modal
      width={withComment ? 900 : undefined}
      title={customer?.name}
      open={open}
      onCancel={onCancel}
      confirmLoading={loading}
      okText="更新"
      footer={[
        !readOnly && (
          <Button
            key="submit"
            type="primary"
            disabled={hitSensitiveWord}
            onClick={() =>
              onSubmit(customer, comment)
                .then(() => onCancel())
                .catch((e) => message.error(e.message))
            }
            loading={loading}
          >
            提交并关闭
          </Button>
        ),
        next && (
          <Button
            disabled={hitSensitiveWord}
            key="back"
            type="primary"
            onClick={() => onSubmitAndNext(comment)}
          >
            下一个并提交
          </Button>
        ),
      ]}
    >
      <Spin spinning={loading}>
        <Row>
          <Col span={withComment ? 14 : 24}>
            <Form
              labelCol={{ span: 4 }}
              wrapperCol={{ span: 16 }}
              form={form}
              initialValues={{
                ...customer,
                extra: generateExtra(customer),
                channel: isSuperAdmin
                  ? customer?.channel
                  : customer?.channel?.replace('RE-', ''),
                mobile: customer?.mobile?.replace(
                  /^([0-9]{4})[0-9]{4}(.*)$/,
                  '$1****$2',
                ),
              }}
              autoComplete="off"
            >
              <Form.Item
                name="mobile"
                label={
                  <Space>
                    <span>电话</span>
                    {showMobile && (
                      <Tooltip title={mobile}>
                        <EyeFill />
                      </Tooltip>
                    )}
                  </Space>
                }
              >
                <Input disabled={!!customer?.mobile} />
              </Form.Item>
              <Form.Item
                name="channel"
                label="渠道"
                rules={[{ required: true }]}
              >
                <Input disabled={true} />
              </Form.Item>
              <Form.Item name="name" label="姓名" rules={[{ required: true }]}>
                <Input disabled={!updateCustomerNamePermission} />
              </Form.Item>
              <Form.Item
                name="aliasName"
                label={
                  systemConfig?.applicationType ===
                  ApplicationTypeEnum.ACTUAL_PAY
                    ? '公司'
                    : '别名'
                }
              >
                <Input readOnly={readOnly} />
              </Form.Item>
              <Form.Item name="gender" label="性别">
                <Radio.Group
                  disabled={readOnly}
                  options={[
                    { label: '男', value: GenderEnum.MAN },
                    { label: '女', value: GenderEnum.WOMEN },
                  ]}
                  optionType="button"
                  buttonStyle="solid"
                />
              </Form.Item>
              <Form.Item
                name="star"
                label={
                  <Space>
                    <Tooltip
                      overlayInnerStyle={{ width: '550px' }}
                      placement="right"
                      title={
                        <div style={{ lineHeight: '15px' }}>
                          <p>
                            0星 待跟进，没有有效沟通或者没有探出任何客户的资质
                          </p>
                          <p>
                            1星
                            资质不符，完全没有可贷条件、自由职业者无资产之类。
                          </p>
                          <p>
                            1星 捣乱申请，
                            <p style={{ paddingLeft: '20px' }}>
                              <p>
                                1.空号、停机、老赖、黑户、执行人、逾期时间很长。
                              </p>
                              <p>2. 态度不好、骂人、要举报、投诉之类。</p>
                              <p>3.异地且不来上海的。</p>
                            </p>
                          </p>
                          <p>
                            2星 待签约，
                            <p style={{ paddingLeft: '20px' }}>
                              <p>1.只有社保</p>
                              <p>2.一辆价值不高的车</p>
                              <p>
                                3.有公积金或者营业执照但是征信不好（逾期或者历史逾期比较严重）
                              </p>
                              <p>以上三者满足其一即可</p>
                            </p>
                          </p>
                          <p>
                            3星 待签约，
                            <p style={{ paddingLeft: '20px' }}>
                              <p>1、本地人（不看征信）</p>
                              <p>2、名下有上海房（不看征信）</p>
                              <p>3、有公积金</p>
                              <p>4、有营业执照</p>
                              <p>以上四者满足其一即可</p>
                            </p>
                          </p>
                          <p>
                            4星 待签约，
                            <p style={{ paddingLeft: '20px' }}>
                              <p>1、优质公积金（公积金1000以上）</p>
                              <p>2、优质上海房（房产市值200以上）</p>
                              <p>3、优质营业执照（有开票纳税）</p>
                              <p>以上三者满足其一即可</p>
                            </p>
                          </p>
                          <p>
                            5星
                            待签约，优质公积金、优质上海房、优质营业执照同时拥有两者及以上
                          </p>
                        </div>
                      }
                    >
                      <InfoCircleOutlined />
                    </Tooltip>
                    <span>星级</span>
                  </Space>
                }
                rules={[
                  ({ getFieldValue }) => ({
                    validator(rule, value, callback) {
                      const state = getFieldValue('state');
                      const starState = convertStarState(value);
                      if (!starState?.length) {
                        return Promise.resolve();
                      }
                      if (!starState.includes(state)) {
                        return Promise.reject('星级不符合当前状态');
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
                dependencies={['state']}
              >
                <Rate disabled={readOnly} />
              </Form.Item>
              <Form.Item
                name="state"
                label={
                  <Space>
                    <Tooltip
                      overlayInnerStyle={{ width: '350px' }}
                      title={
                        <div>
                          <p>以下几类客户遇到星级和状态修改成：</p>
                          <p>1星--捣乱申请</p>
                          <p>1.空号、停机、老赖、黑户、执行人、逾期时间很长。</p>
                          <p>2.态度不好、骂人、要举报、投诉之类。</p>
                          <p>3.异地且不来上海的。</p>
                        </div>
                      }
                    >
                      <InfoCircleOutlined />
                    </Tooltip>
                    <span>状态</span>
                  </Space>
                }
              >
                <CustomerStateSelect
                  enableState={starState}
                  disabled={disableState || readOnly}
                  disableState={[CustomerStateEnum.LOAN]}
                />
              </Form.Item>
              <Form.Item name="group" label="分组">
                <Select options={groupOptions} disabled={readOnly} />
              </Form.Item>
              <Form.Item name="remark" label="其他资质">
                <Input.TextArea rows={5} disabled={readOnly} />
              </Form.Item>
              <Form.Item
                name="extra"
                label="附属信息"
                hidden={
                  systemConfig?.applicationType ===
                  ApplicationTypeEnum.ACTUAL_PAY
                }
              >
                <Checkbox.Group
                  disabled={readOnly}
                  options={Object.entries(extraKeys).map(([value, label]) => ({
                    value,
                    label,
                  }))}
                />
              </Form.Item>
            </Form>
          </Col>
          {withComment && (
            <Col span={10}>
              <Row>
                <Col style={{ width: '100%' }}>
                  <Comment
                    onChange={setComment}
                    value={comment}
                    hitSensitiveWord={hitSensitiveWord}
                  />
                </Col>
                <Col style={{ marginTop: '10px', width: '100%' }}>
                  <Space
                    style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <>{customer?.id && <MakeCall customerId={customer.id} />}</>
                    <Checkbox
                      checked={dropPublicPool}
                      onChange={(v) => {
                        setDropPublicPool(v.target.checked);
                      }}
                    >
                      抛入公共池
                    </Checkbox>
                  </Space>
                </Col>
              </Row>
              {customer?.id && (
                <Row style={{ marginTop: '10px' }}>
                  <Col span={24}>
                    <Space>
                      {[5, 15, 30].map((minute) => {
                        return (
                          <CustomerAlarm
                            key={minute}
                            customerId={customer?.id}
                            minute={minute}
                            msg="处理客户"
                          />
                        );
                      })}
                      <span>分钟后提醒处理</span>
                    </Space>
                  </Col>
                  <Col span={24}>
                    <Space>
                      <CustomerAlarmClock customerId={customer.id} />
                    </Space>
                  </Col>
                </Row>
              )}
              <Divider />
              {customer && (
                // <Spin spinning={eventsLoading}>
                //   <LastEvent
                //     customerId={customer?.id}
                //     customerName={customer?.name}
                //     event={events?.[0]}
                //   />
                // </Spin>
                <Row style={{ marginTop: '10px' }}>
                  <Col span={24}>
                    <div
                      style={{
                        width: '100%',
                        height: '40vh',
                        overflow: 'auto',
                      }}
                    >
                      <EventTimelineAutoLoad
                        isRe={(customer?.count || 0) > 0}
                        pool={customer.pool}
                        customerId={customer?.id}
                        companyId={companyId}
                        customerName={customer?.name}
                      />
                    </div>
                  </Col>
                </Row>
              )}
            </Col>
          )}
        </Row>
      </Spin>
    </Modal>
  );
};
